export default class DateUtils {
	/**
	 * Simple date parser, that parses a date given either as a string in the format of DD.MM.YYYY. or as a number array.
	 * Often, the handed strings/number arrays have been checked beforehand,
	 * so we can make checking the validity optional.
	 * @param input Date as DD.MM.YYYY, e.g. '12.02.2020' or number array, e.g. [12, 02, 2020]
	 * @param checkIsValid Boolean if the handed string should be checked for validity
	 * @returns
	 * * The handed date as JavaScript Date
	 * * `null` if the date wasn't handed in the correct format.
	 */
	static parseDate(input: string, format?: any): Date | null {
		// split the date into year, month and day and map it to number array, if it isn't already given as such
		format = format || this.getCurrentDateFormat(); // default format
		const parts: RegExpMatchArray | null = input.match(/(\d+)/g),
			fmt: { [key: string]: number } = {};
		let i = 0;
		// extract date-part indexes from the format
		format.replace(/(yyyy|dd|mm)/g, function(part: string) {
			fmt[part] = i++;
		});
		if (parts && this.hasValidDateEntries(parts, fmt)) {
			const resultDate = new Date(
				parseInt(parts[fmt['yyyy']]),
				parseInt(parts[fmt['mm']]) - 1,
				parseInt(parts[fmt['dd']]),
			);
			if (resultDate.getTime() === resultDate.getTime()) {
				return resultDate;
			}
		}
		console.error(
			'[DateUtils] Wrong date format! Please enter date in the format: ' + format + '. Entered: ' + input,
		);
		return null;
	}

	/**
	 * Utility function to check year, month and date plausibility
	 */
	static hasValidDateEntries(parts: RegExpMatchArray, fmt: { [key: string]: number }): boolean {
		if (fmt && Object.keys(fmt).length === 0) return false;
		let result = true;
		// inspect the year
		// must contain exactly 4 digits
		result = result && parts[fmt['yyyy']].length === 4;

		// inspect the month
		// must contain a number between 1 and 12
		result = result && parseInt(parts[fmt['mm']]) > 0 && parseInt(parts[fmt['mm']]) < 13;
		// inspect the date
		// must contain a number between 1 and 31
		result = result && parseInt(parts[fmt['dd']]) > 0 && parseInt(parts[fmt['dd']]) < 32;
		return result;
	}

	/**
	 * Simple utility function to compare the months (and years) of two dates.
	 * @param date1 Date 1
	 * @param date2 Date 2
	 * @returns
	 * * `-1` if Date 1 is earlier than Date 2,
	 * * `0` if year and month of both dates are the same
	 * * `1` if Date 1 is later than Date 2
	 */
	static compareMonths(date1: Date, date2: Date): number {
		const year1 = date1.getFullYear(),
			year2 = date2.getFullYear();
		// first check the year:
		// year1 is earlier than year2 --> date1 month is definetly earlier than date2 month
		if (year1 < year2) {
			return -1;
		}
		// date1 year is the exact same as date2 year --> further investigate
		else if (year1 === year2) {
			const month1 = date1.getMonth(),
				month2 = date2.getMonth();
			// month1 is earlier than month2
			if (month1 < month2) {
				return -1;
			}
			// year AND month are the same
			else if (month1 === month2) {
				return 0;
			}
		}
		// year1 is later than year2 --> date1 month is definetly later than date2 month
		// OR only month1 is later than month2, but year is the same --> still month1 is later than month2
		return 1;
	}

	/**
	 * Simple utility function to compare two dates regardless of their times.
	 * @param date1 Date 1
	 * @param date2 Date 2
	 * @returns
	 * * `-1` if Date 1 is earlier than Date 2,
	 * * `0` if both dates are the same
	 * * `1` if Date 1 is later than Date 2
	 */
	static compareDates(date1: Date | null, date2: Date | null): number | null {
		if (!date1 || !date2) {
			return null;
		}
		// create new dates with times set to zero
		// since we're only interested in comparing the dates by means of days
		const iDate1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate(), 0, 0, 0);
		const iDate2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate(), 0, 0, 0);
		const date1Time = iDate1.getTime(),
			date2Time = iDate2.getTime();
		// date1 is earlier than date2
		if (date1Time < date2Time) {
			return -1;
		}
		// the dates are the same
		else if (date1Time === date2Time) {
			return 0;
		}
		// date1 is later than date2
		return 1;
	}

	/**
	 * Simple utility function to return all 12 month names uppercased as array in short format.
	 */
	static getMonthNames(): string[] {
		const monthNos = Array.from({ length: 12 }, (_, i) => i);
		return monthNos.map(function(mon) {
			return new Date(2000, mon).toLocaleString('default', { month: 'short' }).toUpperCase() + '.';
		});
	}

	/**
	 * Simple utility function to return a month's name uppercased and in short format.
	 * @param month Index of the month which name is to be returned. 0-based (0 equals January)
	 */
	static getMonthName(month: number): string {
		// get the month name in short format
		return new Date(2000, month).toLocaleString('default', { month: 'short' }).toUpperCase() + '.';
	}

	/**
	 * Simple utility function to retrieve all weekday names in short format.
	 */
	static getWeekdayNames(): string[] {
		const weekdays = Array.from({ length: 7 }, (_, i) => i);
		// start collecting the names of weekdays from 03.01.2000 (since that's a Monday)
		return weekdays.map(function(day) {
			return new Date(2000, 0, 3 + day).toLocaleString('default', { weekday: 'short' });
		});
	}

	/**
	 * Determines and returns the date format (e.g. `mm/dd/yyyy`) based on the navigator's language.
	 */
	static getCurrentDateFormat() {
		const formats: { [key: string]: string } = {
			'af-za': 'yyyy/mm/dd',
			'ar-ae': 'dd/mm/yyyy',
			'ar-bh': 'dd/mm/yyyy',
			'ar-dz': 'dd-mm-yyyy',
			'ar-eg': 'dd/mm/yyyy',
			'ar-iq': 'dd/mm/yyyy',
			'ar-jo': 'dd/mm/yyyy',
			'ar-kw': 'dd/mm/yyyy',
			'ar-lb': 'dd/mm/yyyy',
			'ar-ly': 'dd/mm/yyyy',
			'ar-ma': 'dd-mm-yyyy',
			'ar-om': 'dd/mm/yyyy',
			'ar-qa': 'dd/mm/yyyy',
			'ar-sy': 'dd/mm/yyyy',
			'ar-tn': 'dd-mm-yyyy',
			'ar-ye': 'dd/mm/yyyy',
			'arn-cl': 'dd-mm-yyyy',
			'as-in': 'dd-mm-yyyy',
			'az-cyrl-az': 'dd.mm.yyyy',
			'az-latn-az': 'dd.mm.yyyy',
			'be-by': 'dd.mm.yyyy',
			'br-fr': 'dd/mm/yyyy',
			'ca-es': 'dd/mm/yyyy',
			'co-fr': 'dd/mm/yyyy',
			'cy-gb': 'dd/mm/yyyy',
			'da-dk': 'dd-mm-yyyy',
			'de-at': 'dd.mm.yyyy',
			'de-ch': 'dd.mm.yyyy',
			'de-de': 'dd.mm.yyyy',
			'de-li': 'dd.mm.yyyy',
			'de-lu': 'dd.mm.yyyy',
			'en-029': 'mm/dd/yyyy',
			'en-bz': 'dd/mm/yyyy',
			'en-ca': 'dd/mm/yyyy',
			'en-gb': 'dd/mm/yyyy',
			'en-ie': 'dd/mm/yyyy',
			'en-in': 'dd-mm-yyyy',
			'en-jm': 'dd/mm/yyyy',
			'en-tt': 'dd/mm/yyyy',
			'en-us': 'mm/dd/yyyy',
			'en-za': 'yyyy/mm/dd',
			'es-ar': 'dd/mm/yyyy',
			'es-bo': 'dd/mm/yyyy',
			'es-cl': 'dd-mm-yyyy',
			'es-co': 'dd/mm/yyyy',
			'es-cr': 'dd/mm/yyyy',
			'es-do': 'dd/mm/yyyy',
			'es-ec': 'dd/mm/yyyy',
			'es-es': 'dd/mm/yyyy',
			'es-gt': 'dd/mm/yyyy',
			'es-hn': 'dd/mm/yyyy',
			'es-mx': 'dd/mm/yyyy',
			'es-ni': 'dd/mm/yyyy',
			'es-pa': 'mm/dd/yyyy',
			'es-pe': 'dd/mm/yyyy',
			'es-pr': 'dd/mm/yyyy',
			'es-py': 'dd/mm/yyyy',
			'es-sv': 'dd/mm/yyyy',
			'es-uy': 'dd/mm/yyyy',
			'es-ve': 'dd/mm/yyyy',
			'eu-es': 'yyyy/mm/dd',
			'fa-ir': 'mm/dd/yyyy',
			'fo-fo': 'dd-mm-yyyy',
			'fr-ca': 'yyyy-mm-dd',
			'fr-ch': 'dd.mm.yyyy',
			'fr-fr': 'dd/mm/yyyy',
			'fr-lu': 'dd/mm/yyyy',
			'fr-mc': 'dd/mm/yyyy',
			'ga-ie': 'dd/mm/yyyy',
			'gd-gb': 'dd/mm/yyyy',
			'gsw-fr': 'dd/mm/yyyy',
			'he-il': 'dd/mm/yyyy',
			'hi-in': 'dd-mm-yyyy',
			'hu-hu': 'yyyy. mm. dd.',
			'hy-am': 'dd.mm.yyyy',
			'id-id': 'dd/mm/yyyy',
			'it-ch': 'dd.mm.yyyy',
			'it-it': 'dd/mm/yyyy',
			'ja-jp': 'yyyy/mm/dd',
			'ka-ge': 'dd.mm.yyyy',
			'kk-kz': 'dd.mm.yyyy',
			'kl-gl': 'dd-mm-yyyy',
			'km-kh': 'yyyy-mm-dd',
			'ko-kr': 'yyyy-mm-dd',
			'kok-in': 'dd-mm-yyyy',
			'lb-lu': 'dd/mm/yyyy',
			'lo-la': 'dd/mm/yyyy',
			'lt-lt': 'yyyy.mm.dd',
			'lv-lv': 'yyyy.mm.dd.',
			'mi-nz': 'dd/mm/yyyy',
			'mk-mk': 'dd.mm.yyyy',
			'mr-in': 'dd-mm-yyyy',
			'ms-bn': 'dd/mm/yyyy',
			'ms-my': 'dd/mm/yyyy',
			'mt-mt': 'dd/mm/yyyy',
			'nb-no': 'dd.mm.yyyy',
			'nn-no': 'dd.mm.yyyy',
			'nso-za': 'yyyy/mm/dd',
			'oc-fr': 'dd/mm/yyyy',
			'pl-pl': 'yyyy-mm-dd',
			'pt-pt': 'dd-mm-yyyy',
			'qut-gt': 'dd/mm/yyyy',
			'quz-bo': 'dd/mm/yyyy',
			'quz-ec': 'dd/mm/yyyy',
			'quz-pe': 'dd/mm/yyyy',
			'rm-ch': 'dd/mm/yyyy',
			'ro-ro': 'dd.mm.yyyy',
			'ru-ru': 'dd.mm.yyyy',
			'sa-in': 'dd-mm-yyyy',
			'sah-ru': 'mm.dd.yyyy',
			'se-no': 'dd.mm.yyyy',
			'se-se': 'yyyy-mm-dd',
			'si-lk': 'yyyy-mm-dd',
			'sma-no': 'dd.mm.yyyy',
			'sma-se': 'yyyy-mm-dd',
			'smj-no': 'dd.mm.yyyy',
			'smj-se': 'yyyy-mm-dd',
			'sq-al': 'yyyy-mm-dd',
			'sv-se': 'yyyy-mm-dd',
			'syr-sy': 'dd/mm/yyyy',
			'ta-in': 'dd-mm-yyyy',
			'tn-za': 'yyyy/mm/dd',
			'tr-tr': 'dd.mm.yyyy',
			'tt-ru': 'dd.mm.yyyy',
			'tzm-latn-dz': 'dd-mm-yyyy',
			'uk-ua': 'dd.mm.yyyy',
			'ur-pk': 'dd/mm/yyyy',
			'uz-cyrl-uz': 'dd.mm.yyyy',
			'uz-latn-uz': 'dd/mm yyyy',
			'vi-vn': 'dd/mm/yyyy',
			'wo-sn': 'dd/mm/yyyy',
			'xh-za': 'yyyy/mm/dd',
			'zu-za': 'yyyy/mm/dd',
		};
		if (typeof window !== 'undefined') {
			return formats[window.navigator.language.toLowerCase()] || 'dd.mm.yyyy';
		}
		return 'dd.mm.yyyy';
	}

	/**
	 * Format a given date into a given format.
	 * E.g. if the date is the 24th of March 2010, and the format is given as yyyy-mm-dd,
	 * the result would be '2010-03-24'.
	 * @param input The date that's supposed to be formatted
	 * @param format The format, given e.g. as 'yyyy-mm-dd'
	 */
	static formatDate(input: Date, format?: string): string {
		// split the date into year, month and day and map it to number array, if it isn't already given as such
		format = format || this.getCurrentDateFormat(); // default format
		let result = format.toLowerCase();

		// replace year
		result.indexOf('yyyy') > -1 && (result = result.replace('yyyy', input.getFullYear().toString()));

		// replace month (caution: date's month is zero-based!)
		if (result.indexOf('mm') > -1) {
			let month = (input.getMonth() + 1).toString();
			if (month.length === 1) {
				month = '0' + month;
			}
			result = result.replace('mm', month);
		}

		//replace date
		if (result.indexOf('dd') > -1) {
			let date = input.getDate().toString();
			if (date.length === 1) {
				date = '0' + date;
			}
			result = result.replace('dd', date);
		}

		// if the format wasn't given correctly: simply return the date as locally formatted string
		if (result.includes('d') || result.includes('m') || result.includes('y')) {
			console.error(
				'[DateUtils] Wrong date format! Format should be given with unicode characters, but was given as',
				format,
				'. Only full-digit formats are supported, e.g. yyyy-mm-dd but not yy-m-d!',
			);
			return input.toLocaleDateString('default', {
				month: '2-digit',
				day: '2-digit',
				year: 'numeric',
			});
		}
		return result;
	}

	/**
	 * Format seconds to the format mm:ss, with a leading zero for digits under 10.
	 * For example if `seconds` is 123, the result would be 02:03.
	 * @param seconds The amount of seconds that's supposed to be formatted
	 */
	static formatSeconds(seconds: number) {
		let result = '';
		const min = Math.floor(seconds / 60);
		min < 10 ? (result += '0' + min) : (result += min.toString());
		result += ':';
		const sec = seconds % 60;
		sec < 10 ? (result += '0' + sec) : (result += sec.toString());
		return result;
	}

	/*
	 * This utility function is necessary because setting the month via JavaScript's Date is error prone:
	 * E.g. When we want to update the date 2021-03-31 to February, in Date it would return 2021-03-03.
	 * Why? Because February doesn't have 31 days so instead the result flips to March.
	 * For some purposes, we want to keep the month, e.g. in the example, we would want 2021-02-28 as a result.
	 * @param input The date, which's month should be set
	 * @param newMonth The new month - 0-based (e.g. January = 0, December = 11)
	 */
	static setMonth(input: Date, newMonth: number): Date {
		let helperdate = new Date(input);
		// all dates from 1st to 28th can be set directly since they occur in ALL months
		helperdate.setMonth(newMonth);
		// after setting the month, the helper date's month should equal the new month
		// otherwise it means, that the date of the input date doesn't exist in the new month (e.g. 31st of February)
		if (helperdate.getMonth() !== newMonth) {
			// trick: 0 is always the last day of the previous month --> e.g. new Date(2021, 3, 0) returns 2021-02-28
			helperdate = new Date(input.getFullYear(), newMonth + 1, 0);
		}
		return helperdate;
	}
}
